"use client";

import { useFeatureFlag } from "@posthog/next";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetOrg, GetOrgFeatures } from "@/app/api/org/getOrg";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { DEFAULT_BRAND } from "@/enums/brands";
import { SupportedCountries, SupportedLanguages } from "@/enums/internationalization";
import { GPT_VERSION, PRODUCT_NAME } from "@/enums/profile";
import { SubscriptionStatus } from "@/enums/stripe";
import { GailProductRoles, UserRoles } from "@/enums/user";
import { Org, OrgFeatures, OrgRequest, getDefaultOrg } from "@/types/org";
import { AgentProfile, AgentProfileRequest } from "@/types/profile";
import { GetAgentProfile, UpdateAgentProfile } from "@/utils/agencies/profiles";
import { DEFAULT_VOICE_ID } from "@/utils/agencies/voices";
import { apiFetch } from "@/lib/api";
import { ProductRolesState } from "@/utils/common/auth";
import { GAIL_API } from "@/utils/common/constants/routes";
import { mergeOrgFeatures, patchOrgFeatures, preserveOrgOverlayFeatures } from "@/utils/org/orgFeatures";
import { orgHasGailAgentEnabled } from "@/utils/reporting/access";
import { useAlert } from "./useAlert";

type UiTypes = {
  locale: Intl.Locale;
  country: SupportedCountries;
  brand: string;
  allowedCountries: SupportedCountries[];
};

export type ProductAccessState = {
  loading: boolean;
  gailGptEnabled: boolean;
  gailAgentEnabled: boolean;
  reportEnabled: boolean;
  userCanUseGailGpt: boolean;
  userCanUseGailAgent: boolean;
  userCanUseReport: boolean;
  userHasAnyProductAccess: boolean;
  orgHasActiveProducts: boolean;
  userIsNewUser: boolean;
  isOrgGailGptV1: boolean;
  allProductsCanceled: boolean;
};

type OrgProps = {
  ui: UiTypes;
  org: Org;
  agentProfile: AgentProfile;
  saveOrg: (o: OrgRequest, options?: { silent?: boolean }) => Promise<void>;
  saveOrgFeatures: (
    features: Partial<OrgFeatures>,
    options?: { silent?: boolean }
  ) => Promise<void>;
  /** Resolves `true` when the profile was persisted; callers that mutate local state first can roll back on `false`. */
  saveAgentProfile: (profile: AgentProfileRequest, options?: { silent?: boolean }) => Promise<boolean>;
  /**
   * True after `GetOrg()` + `GetOrgFeatures()` have finished (or there is no
   * agency to load). Cached `orgData` is not enough for versioning gates.
   */
  orgFeaturesLoaded: boolean;
  /** Refetches org + overlay features. Resolves the merged org, or null if GetOrg failed. */
  refreshOrg: () => Promise<Org | null>;
  isGailGptOnlyOrg: () => boolean;
  productAccess: ProductAccessState;
  role: UserRoles;
  productRolesState: ProductRolesState;
};

const getDefaultProfile = (): AgentProfile => {
  return {
    frequentlyAskedQuestions: [],
    operationSchedule: {
      timeZone: "America/New_York",
      businessSchedule: [],
      outboundSchedule: [],
    },
    languages: [SupportedLanguages.English],
    voice: DEFAULT_VOICE_ID,
  };
};

const getDefaultProductAccessState = (): ProductAccessState => ({
  loading: true,
  gailGptEnabled: false,
  gailAgentEnabled: false,
  reportEnabled: false,
  userCanUseGailGpt: false,
  userCanUseGailAgent: false,
  userCanUseReport: false,
  userHasAnyProductAccess: false,
  orgHasActiveProducts: false,
  userIsNewUser: false,
  isOrgGailGptV1: false,
  allProductsCanceled: false,
});

const OrgContext = createContext<OrgProps>({
  ui: {
    locale: new Intl.Locale("en-US"),
    country: SupportedCountries.UnitedStates,
    brand: DEFAULT_BRAND,
    allowedCountries: [SupportedCountries.UnitedStates],
  },
  org: getDefaultOrg(),
  agentProfile: getDefaultProfile(),
  saveOrg: async () => {},
  saveOrgFeatures: async () => {},
  saveAgentProfile: async () => false,
  orgFeaturesLoaded: false,
  refreshOrg: async () => null,
  isGailGptOnlyOrg: () => false,
  productAccess: getDefaultProductAccessState(),
  role: UserRoles.User,
  productRolesState: { roles: undefined, loaded: false },
});

export const useOrg = () => useContext(OrgContext);

export const computeProductAccessState = (
  currentOrg: Org,
  currentProductRoles: GailProductRoles[]
): ProductAccessState => {
  const isOrgGailGptV1 =
    currentOrg?.features?.gptVersion?.toLowerCase() === GPT_VERSION.V1.toLowerCase();

  const orgHasActiveOrTrialGailGPT =
    currentOrg?.products?.some(
      (product) =>
        product.name === PRODUCT_NAME.GailGPT &&
        (product.subscriptionStatus === SubscriptionStatus.active ||
          product.subscriptionStatus === SubscriptionStatus.trialing)
    ) ?? false;

  const isGailAgentProduct = (product: {name?: string; productOffering?: string}) =>
    product.name === PRODUCT_NAME.GailAgent || product.productOffering === PRODUCT_NAME.GailAgent;

  const orgHasActiveOrTrialGailAgent =
    currentOrg?.products?.some(
      (product) =>
        isGailAgentProduct(product) &&
        (product.subscriptionStatus === SubscriptionStatus.active ||
          product.subscriptionStatus === SubscriptionStatus.trialing)
    ) ?? false;

  let gailGptEnabled: boolean;
  const reportEnabled = !!currentOrg.details?.reporting?.collectionId;
  // Keep client gating aligned with server `orgHasGailAgentEnabled` (name or productOffering + V1)
  const gailAgentEnabled = orgHasGailAgentEnabled(currentOrg);

  if (isOrgGailGptV1) {
    gailGptEnabled = true;
  } else {
    gailGptEnabled = currentOrg.products.some((product) => product.name === PRODUCT_NAME.GailGPT);
  }

  const userCanUseGailGpt =
    gailGptEnabled &&
    (orgHasActiveOrTrialGailGPT || isOrgGailGptV1) &&
    (currentProductRoles.includes(GailProductRoles.GailGPTUser) || isOrgGailGptV1);

  const userCanUseGailAgent =
    gailAgentEnabled &&
    (orgHasActiveOrTrialGailAgent || isOrgGailGptV1) &&
    (currentProductRoles.includes(GailProductRoles.GailAgentUser) || isOrgGailGptV1);

  const userCanUseReport =
    reportEnabled && currentProductRoles.includes(GailProductRoles.ReportUser);

  const userHasAnyProductAccess =
    userCanUseGailAgent || userCanUseGailGpt || isOrgGailGptV1 || userCanUseReport;
  const userIsNewUser = !userHasAnyProductAccess;
  const orgHasActiveProducts = orgHasActiveOrTrialGailAgent || orgHasActiveOrTrialGailGPT;

  const allProductsCanceled =
    currentOrg?.products?.length > 0 &&
    currentOrg.products.every(
      (product) => product.subscriptionStatus === SubscriptionStatus.canceled
    );

  return {
    loading: false,
    gailGptEnabled,
    gailAgentEnabled,
    reportEnabled,
    userCanUseGailGpt,
    userCanUseGailAgent,
    userCanUseReport,
    userHasAnyProductAccess,
    orgHasActiveProducts,
    userIsNewUser,
    isOrgGailGptV1,
    allProductsCanceled,
  };
};

// A GailGPT-only org has no agent surfaces and nothing outside them reads the profile, so the
// request is pure waste on every authenticated page load. Skipping it costs one serialized
// round-trip for agent orgs, which is why the guard is worth its sequencing.
const orgUsesGailAgent = (candidate: Org): boolean =>
  candidate.features?.gptVersion?.toLowerCase() === GPT_VERSION.V1.toLowerCase() ||
  candidate.products.some(
    (product) =>
      (product.name === PRODUCT_NAME.GailAgent ||
        product.productOffering === PRODUCT_NAME.GailAgent) &&
      (product.subscriptionStatus === SubscriptionStatus.active ||
        product.subscriptionStatus === SubscriptionStatus.trialing)
  );

const useOrgFunction = (): OrgProps => {
  const { productRolesState, role, user } = useAuthContext();
  const { setIsSaving, setAlert } = useAlert();
  const { t: ct } = useTranslation("common");
  const allowedCountriesFlag = useFeatureFlag("ui-allowed-countries");

  const [profile, setProfile] = useState<AgentProfile>(getDefaultProfile());
  const [org, setOrg] = useState<Org>(getDefaultOrg());
  const [uiData, setUiData] = useState<UiTypes>(() => ({
    locale: new Intl.Locale("en-US"),
    country: SupportedCountries.UnitedStates,
    // Always DEFAULT_BRAND on first paint so SSR matches hydration; localStorage
    // brand is applied in the mount effect below.
    brand: DEFAULT_BRAND,
    allowedCountries: [SupportedCountries.UnitedStates],
  }));

  // Product access state management
  const [productAccessState, setProductAccessState] = useState<ProductAccessState>(
    getDefaultProductAccessState()
  );
  const [orgDataLoaded, setOrgDataLoaded] = useState(false);
  const [orgFeaturesLoaded, setOrgFeaturesLoaded] = useState(false);

  // Update product access state when dependencies change
  useEffect(() => {
    // Keep default loading: true state until both auth roles and org product
    // data are available. On hard refresh, auth roles are server-seeded before
    // org data hydrates; computing against the default empty org would briefly
    // revoke product access and trigger route guards.
    if (!productRolesState.loaded || !orgDataLoaded) {
      setProductAccessState((current) =>
        current.loading ? current : { ...current, loading: true }
      );
      return;
    }
    setProductAccessState(computeProductAccessState(org, productRolesState.roles ?? []));
  }, [org, orgDataLoaded, productRolesState.roles, productRolesState.loaded]);

  const updateFromOrg = (newOrg: Org) => {
    const newBrand = newOrg.details?.brand ?? DEFAULT_BRAND;
    if (globalThis.window !== undefined) {
      globalThis.localStorage.setItem("brand", newBrand);
    }
    const newLanguage =
      newOrg.localization && newOrg.localization.split("-").length > 1
        ? (newOrg.localization.split("-")[0] as SupportedLanguages)
        : SupportedLanguages.English;
    const newCountry =
      newOrg.localization && newOrg.localization.split("-").length > 1
        ? (newOrg.localization.split("-")[1] as SupportedCountries)
        : SupportedCountries.UnitedStates;

    setUiData((prev) => ({
      locale: new Intl.Locale(newLanguage + "-" + newCountry),
      country: newCountry,
      brand: newBrand,
      allowedCountries: prev.allowedCountries,
    }));

    setOrg(newOrg);
  };

  const fetchResources = async (): Promise<Org | null> => {
    // API calls use cookies automatically via credentials: 'include'
    const [newOrg, features] = await Promise.all([GetOrg(), GetOrgFeatures()]);

    // Merged up-front because gptVersion can arrive from the features endpoint and decides whether
    // this is a legacy org with agent access.
    if (newOrg) {
      Object.assign(newOrg, mergeOrgFeatures(newOrg, features));
    }

    // Sequenced after the org because the org's products decide whether the profile is wanted.
    const expectsAgentProfile = !!newOrg && orgUsesGailAgent(newOrg);
    const newAgentProfile = expectsAgentProfile ? await GetAgentProfile() : null;

    if (!newOrg || (expectsAgentProfile && !newAgentProfile)) {
      setAlert({
        status: "error",
        message: ct("failedLoadingOrgSettings"),
        secondsToClose: 10,
      });
    }

    let needsReload = false;

    if (newOrg) {
      const newBrand = newOrg.details?.brand ?? DEFAULT_BRAND;
      const existingBrand = globalThis.localStorage.getItem("brand") || DEFAULT_BRAND;

      if (newBrand !== existingBrand) {
        globalThis.localStorage.setItem("brand", newBrand);
        needsReload = true;
      }

      updateFromOrg(newOrg);
      globalThis.localStorage.setItem("orgData", JSON.stringify(newOrg));
    }

    if (newAgentProfile) {
      setProfile(newAgentProfile);
      globalThis.localStorage.setItem("agentProfile", JSON.stringify(newAgentProfile));
    } else if (!expectsAgentProfile) {
      // The mount effect hydrates the profile from localStorage before the org is known, so
      // skipping the fetch would otherwise leave a GPT-only org holding whichever agent config the
      // previous org cached. Only reset when the profile was never wanted — a failed fetch keeps
      // the cache rather than wiping real config on a transient error.
      setProfile(getDefaultProfile());
      globalThis.localStorage.removeItem("agentProfile");
    }

    if (newOrg) {
      setOrgDataLoaded(true);
    }

    setOrgFeaturesLoaded(true);

    if (needsReload) {
      globalThis.location.reload();
    }

    return newOrg ?? null;
  };

  useEffect(() => {
    if (user?.agencyId) {
      setOrgDataLoaded(false);
      setOrgFeaturesLoaded(false);
      void fetchResources();
    } else {
      setProfile(getDefaultProfile());
      setOrg(getDefaultOrg());
      setOrgDataLoaded(true);
      setOrgFeaturesLoaded(true);
    }
  }, [user?.agencyId]);

  useEffect(() => {
    const cachedProfile = globalThis.localStorage.getItem("agentProfile");
    const orgFromCache = globalThis.localStorage.getItem("orgData");

    let locale =
      navigator.languages && navigator.languages.length > 0 ? navigator.languages[0] : "en-US";

    let country =
      locale.split("-").length > 1
        ? (locale.split("-")[1] as SupportedCountries)
        : SupportedCountries.UnitedStates;

    let language =
      locale.split("-").length > 1
        ? (locale.split("-")[0] as SupportedLanguages)
        : SupportedLanguages.English;

    if (!Object.values(SupportedLanguages).includes(locale.split("-")[0] as SupportedLanguages)) {
      locale = "en-US";
    }

    if (!Object.values(SupportedCountries).includes(country)) {
      country = SupportedCountries.UnitedStates;
    }

    const brand = (globalThis.localStorage.getItem("brand") as string) ?? DEFAULT_BRAND;

    setUiData((prev) => ({
      locale: new Intl.Locale(language + "-" + country),
      country,
      brand,
      allowedCountries: prev.allowedCountries,
    }));

    if (cachedProfile) {
      const newProfile = JSON.parse(cachedProfile) as AgentProfile;

      setProfile(newProfile);
    } else {
      setProfile({
        ...profile,
        languages: [language],
      });
    }

    // Drop a cached org whose id doesn't match the auth token's org_id —
    // otherwise org-scoped requests (e.g. Wingman X-Org-Id) race the fresh
    // fetch and 403 with a token/header mismatch. user.orgId is `string | null`,
    // and a null claim still can't authorize a cached id, so any cached org
    // is stale when the auth user has no orgId.
    const cachedOrg = orgFromCache ? (JSON.parse(orgFromCache) as Org) : null;
    const isCachedOrgStale = !!(cachedOrg?.id && user && cachedOrg.id !== user.orgId);
    if (isCachedOrgStale) {
      globalThis.localStorage.removeItem("orgData");
    }
    if (cachedOrg && !isCachedOrgStale) {
      updateFromOrg(cachedOrg);
      setOrgDataLoaded(true);
    } else {
      setOrg({
        ...getDefaultOrg(),
        localization: locale,
      });
      if (!user?.agencyId) {
        setOrgDataLoaded(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!allowedCountriesFlag?.enabled) return;

    const countries = allowedCountriesFlag.payload as SupportedCountries[] | undefined;

    if (!countries || countries.length === 0) {
      setUiData((prev) => ({
        ...prev,
        allowedCountries: Object.values(SupportedCountries),
      }));
    } else {
      setUiData((prev) => ({
        ...prev,
        allowedCountries: countries,
      }));
    }
  }, [allowedCountriesFlag]);

  const saveAgentProfile = async (
    updatedProfile: AgentProfileRequest,
    options?: { silent?: boolean }
  ): Promise<boolean> => {
    const silent = options?.silent ?? false;

    if (!silent) {
      setIsSaving(true, ct("savingOrgSettings"));
    }

    // API calls use cookies automatically via credentials: 'include'
    const newProfile = await UpdateAgentProfile(updatedProfile);

    if (!silent) {
      setIsSaving(false, "");
    }

    if (newProfile) {
      if (!silent) {
        setAlert({
          status: "success",
          message: ct("savedOrgSettings"),
          secondsToClose: 4,
        });
      }
      setProfile(newProfile);
      globalThis.localStorage.setItem("agentProfile", JSON.stringify(newProfile));
      return true;
    }
    if (!silent) {
      setAlert({
        status: "error",
        message: ct("failedOrgSettings"),
        secondsToClose: 10,
      });
    }
    return false;
  };

  const saveOrg = async (updateOrg: OrgRequest, options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;

    if (!silent) {
      setIsSaving(true, ct("savingOrgSettings"));
    }

    // API calls use cookies automatically via credentials: 'include'
    const updatedOrg = await apiFetch<Org>(
      `${GAIL_API}/.internal/customer/organization`,
      "PATCH",
      {
      body: updateOrg,
    },
    );

    if (!silent) {
      setIsSaving(false, "");
    }

    if (updatedOrg) {
      const orgToStore = preserveOrgOverlayFeatures(org, updatedOrg);
      if (!silent) {
        setAlert({
          status: "success",
          message: ct("savedOrgSettings"),
          secondsToClose: 10,
        });
      }
      globalThis.localStorage.setItem("orgData", JSON.stringify(orgToStore));
      updateFromOrg(orgToStore);

      if (updatedOrg.name && updatedOrg.name !== org.name) {
        await apiFetch<{ success: boolean }>(
          "/api/org/update-display-name",
          "PATCH",
          {
          body: {
            display_name: updatedOrg.name,
          },
          silent: true,
        },
        );
      }
    } else if (!silent) {
      setAlert({
        status: "error",
        message: ct("failedOrgSettings"),
        secondsToClose: 10,
      });
    }
  };

  const saveOrgFeatures = async (
    features: Partial<OrgFeatures>,
    options?: { silent?: boolean }
  ) => {
    await patchOrgFeatures(
      org,
      features,
      {
        onSavingStart: () => setIsSaving(true, ct("savingOrgSettings")),
        onSavingEnd: () => setIsSaving(false, ""),
        onSuccess: (updatedOrg) => {
          setAlert({
            status: "success",
            message: ct("savedOrgSettings"),
            secondsToClose: 10,
          });
          updateFromOrg(updatedOrg);
        },
        onError: () => {
          setAlert({
            status: "error",
            message: ct("failedOrgSettings"),
            secondsToClose: 10,
          });
        },
      },
      options
    );
  };

  const isGailGptOnlyOrg = () => {
    return (
      productAccessState.userCanUseGailGpt &&
      !productAccessState.userCanUseGailAgent &&
      productAccessState.gailGptEnabled
    );
  };

  return {
    ui: uiData,
    org,
    agentProfile: profile,
    saveOrg,
    saveOrgFeatures,
    saveAgentProfile,
    orgFeaturesLoaded,
    refreshOrg: fetchResources,
    isGailGptOnlyOrg,
    productAccess: productAccessState,
    role,
    productRolesState,
  };
};

export const OrgContextProvider = ({ children }: { children: React.ReactNode }) => {
  const profileFunction = useOrgFunction();

  return <OrgContext.Provider value={profileFunction}>{children}</OrgContext.Provider>;
};
